import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { isValid } from "date-fns/isValid";
import { optimisticallyUpdateSyncStatusToRunning } from "~api/sync.ts";
import { useToast } from "~components/shared/useToast.tsx";
import { useCurrentTenant } from "~contexts/CurrentTenantContext/CurrentTenantContext.tsx";
import { useTranslation } from "~contexts/I18nContext/I18nContext.tsx";
import type {
  ConstructionSite,
  ConstructionSiteDetail,
  Tenant,
  UpdateConstructionSitePlan,
} from "~generated";
import { ConstructionSitesService } from "~generated";
import type { ConstructionSitesMetrics } from "~generated/models/ConstructionSitesMetrics";
import type { QueryOpts } from "./helpers";
import { teamAssignmentKeys } from "./teamAssignmentKeys";
import { tenantKeys } from "./tenants";

export const constructionSiteKeys = {
  base: (tenantId: string) =>
    [...tenantKeys.detail(tenantId), "constructionSites", "v1"] as const,
  list: (tenantId: string, includeDeleted: boolean) =>
    [...constructionSiteKeys.base(tenantId), "list", includeDeleted] as const,
  show: (
    tenantId: string,
    id: string,
    params: { actualMinutesFromDate?: Date; actualMinutesFromToDate?: Date },
  ) => [...constructionSiteKeys.base(tenantId), "show", id, params] as const,
  syncStatus: (tenantId: string) =>
    [...constructionSiteKeys.base(tenantId), "syncStatus"] as const,
  metrics: (tenantId: string) =>
    [...constructionSiteKeys.base(tenantId), "metrics"] as const,
};

async function getConstructionSite({
  tenantId,
  id,
  actualMinutesFromToDate,
  actualMinutesFromDate,
}: {
  tenantId: Tenant["id"];
  id: ConstructionSite["id"];
  actualMinutesFromDate?: Date;
  actualMinutesFromToDate?: Date;
}) {
  const constructionSite = await ConstructionSitesService.getConstructionSite(
    tenantId,
    id,
    actualMinutesFromDate && isValid(actualMinutesFromDate)
      ? actualMinutesFromDate.toISOString()
      : undefined,
    actualMinutesFromToDate && isValid(actualMinutesFromToDate)
      ? actualMinutesFromToDate.toISOString()
      : undefined,
  );
  return constructionSite;
}

async function getConstructionSites({
  tenantId,
  includeDeleted,
}: {
  tenantId: Tenant["id"];
  includeDeleted: boolean;
}) {
  const constructionSites = await ConstructionSitesService.getConstructionSites(
    tenantId,
    includeDeleted,
  );
  if (!constructionSites?.construction_sites) {
    return [];
  }
  return constructionSites.construction_sites;
}

interface UseConstructionSitesQueryOptions {
  includeDeleted?: boolean;
  useQueryOptions?: QueryOpts<
    ConstructionSite[],
    ReturnType<typeof constructionSiteKeys.list>
  >;
}

export function useConstructionSites(
  {
    includeDeleted = false,
    useQueryOptions,
  }: UseConstructionSitesQueryOptions = { includeDeleted: false },
) {
  const currentTenant = useCurrentTenant();

  const queryResult = useQuery({
    ...useQueryOptions,
    queryKey: constructionSiteKeys.list(currentTenant.id, includeDeleted),
    queryFn: async () =>
      getConstructionSites({
        tenantId: currentTenant.id,
        includeDeleted,
      }),
  });

  return {
    ...queryResult,
    data: queryResult.data || [],
  };
}

export function useConstructionSite(
  id: string,
  actualMinutesFromDate?: Date,
  actualMinutesFromToDate?: Date,
  useQueryOptions?: QueryOpts<
    ConstructionSiteDetail,
    ReturnType<typeof constructionSiteKeys.show>
  >,
) {
  const currentTenant = useCurrentTenant();

  return useQuery({
    ...useQueryOptions,
    queryKey: constructionSiteKeys.show(currentTenant.id, id, {
      actualMinutesFromDate,
      actualMinutesFromToDate,
    }),
    queryFn: async () =>
      getConstructionSite({
        tenantId: currentTenant.id,
        id,
        actualMinutesFromDate,
        actualMinutesFromToDate,
      }),
  });
}

export function useConstructionSitesSyncStatus() {
  const currentTenant = useCurrentTenant();

  return useQuery({
    queryKey: constructionSiteKeys.syncStatus(currentTenant.id),
    queryFn: async () =>
      ConstructionSitesService.getConstructionSitesSyncStatus(currentTenant.id),
    refetchInterval: 5000,
  });
}

export function useSyncConstructionSites() {
  const queryClient = useQueryClient();
  const currentTenant = useCurrentTenant();

  return useMutation({
    mutationFn: async () =>
      ConstructionSitesService.syncConstructionSites(currentTenant.id),

    onMutate: () => {
      optimisticallyUpdateSyncStatusToRunning({
        queryKey: constructionSiteKeys.syncStatus(currentTenant.id),
        queryClient,
      });
    },
  });
}

export interface UpdateConstructionSitePlanProps {
  id: string;
  requestBody?: UpdateConstructionSitePlan;
}

export function useUpdateConstructionSitePlan() {
  const { showSuccessToast } = useToast();
  const t = useTranslation();
  const currentTenant = useCurrentTenant();

  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async ({ id, requestBody }: UpdateConstructionSitePlanProps) =>
      ConstructionSitesService.updateConstructionSitePlan(
        currentTenant.id,
        id,
        requestBody,
      ),

    onSuccess: async () => {
      showSuccessToast(t.editSuccess);
      await queryClient.invalidateQueries({
        queryKey: teamAssignmentKeys.list(currentTenant.id),
      });
    },
  });
  return mutation;
}

export function useConstructionSitesMetrics(
  useQueryOptions?: QueryOpts<
    ConstructionSitesMetrics,
    ReturnType<typeof constructionSiteKeys.metrics>
  >,
) {
  const currentTenant = useCurrentTenant();

  return useQuery({
    ...useQueryOptions,
    queryKey: constructionSiteKeys.metrics(currentTenant.id),
    queryFn: async () =>
      ConstructionSitesService.getConstructionSitesMetrics(currentTenant.id),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}
